/**
 * Use Dashboard Counts Hook
 * Custom hook for managing dashboard counter state with caching and realtime updates
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { type Counts, type RealtimeMessage } from "./types";
import {
  getCache,
  isCacheValid,
  fetchDashboardCounts as fetchCounts,
  setCache,
} from "./countsCache";
import { useRealtime } from "../../reception/useRealtimeHook";
import { useUserProfile } from "./useUserProfile";

interface UseDashboardCountsOptions {
  counts?: Partial<Counts>;
}

const MIN_BYPASS_REFRESH_INTERVAL_MS = 5000;

const COUNTER_EVENT_PREFIXES: readonly string[] = [
  "student_",
  "group_",
  "request_",
  "course_interest_",
  "invoice_",
  "installment_",
  "placement_payment_",
  "refund_",
  "sales_invoice_",
  "sales_consult_",
  "employee_",
  "sales_lead_",
  "finance_income_",
  "finance_expense_",
];

const COUNTER_EVENT_EXACT = new Set<string>([
  "new",
  "refund_update",
  "sales_consult_queue_updated",
]);

const COUNTER_TABLE_HINTS = new Set<string>([
  "students",
  "student_waiting_requests",
  "student_branch_transfers",
  "groups",
  "group_members",
  "group_memberships",
  "group_join_requests",
  "course_interest_requests",
  "course_interests",
  "invoices",
  "general_student_invoices",
  "invoice_installments",
  "pending_sales_invoices",
  "placement_payments",
  "refund_requests",
  "finance_income",
  "finance_incomes",
  "finance_expense",
  "finance_expenses",
  "sales_consultation_slots",
  "sales_lead_consultation_choices",
  "employees",
  "sales_leads",
]);

export function eventAffectsSidebarCounters(eventType: string, tableName: string): boolean {
  if (!eventType && !tableName) return false;
  if (COUNTER_EVENT_EXACT.has(eventType)) return true;
  if (COUNTER_EVENT_PREFIXES.some((prefix) => eventType.startsWith(prefix))) return true;
  if (tableName && COUNTER_TABLE_HINTS.has(tableName)) return true;
  return false;
}

export function useDashboardCounts({ counts }: UseDashboardCountsOptions) {
  const [ownCounts, setOwnCounts] = useState<Counts | undefined>(undefined);
  const realtime = useRealtime();
  const countsRefreshTimer = useRef<number | null>(null);
  const lastBypassRefreshAtRef = useRef(0);
  const {
    userBranch,
    showSuper,
    showLanguage,
    profileResolved = false,
  } = useUserProfile();

  // For regular reception, pass empty string (inclusive) for sidebar counts
  // This matches default backend behavior (Employee Branch + Online).
  // Super users also get global view (empty string).
  const branchParam =
    showSuper || !showLanguage ? "" : userBranch?.trim() || "";
  const scopedBranchRequired = !showSuper && !showLanguage;
  const resourceScopeReady = profileResolved
    && (!scopedBranchRequired || Boolean(userBranch.trim()));
  const lastBranchRef = useRef<string | null>(null);

  // If parent passes counts, update shared cache
  useEffect(() => {
    if (!resourceScopeReady || !counts) return;
    const anyDefined = Object.values(counts).some((v) => typeof v === "number");
    if (anyDefined) {
      setCache(branchParam, counts as Counts);
    }
  }, [counts, branchParam, resourceScopeReady]);

  const fetchDashboardCounts = useCallback(
    async (bypass?: boolean) => {
      if (!resourceScopeReady) return;
      try {
        const data = await fetchCounts(branchParam, bypass);
        setOwnCounts(data);
      } catch {
        // Ignore network/parse issues silently
      }
    },
    [branchParam, resourceScopeReady]
  );

  // Clear local counts when role/branch context changes
  useEffect(() => {
    if (counts) return;
    const key = branchParam;
    if (lastBranchRef.current !== key) {
      lastBranchRef.current = key;
      setOwnCounts(undefined);
    }
  }, [branchParam, counts]);

  useEffect(() => {
    if (counts || !resourceScopeReady) {
      if (!resourceScopeReady) setOwnCounts(undefined);
      return;
    }

    const cache = getCache(branchParam);
    const cacheValid = isCacheValid(branchParam);
    if (cache) {
      setOwnCounts(cache.data);
      if (!cacheValid) {
        fetchDashboardCounts().catch(() => {
          /* ignore */
        });
      }
      return;
    }
    fetchDashboardCounts().catch(() => {
      /* ignore */
    });
  }, [counts, branchParam, fetchDashboardCounts, resourceScopeReady]);

  const scheduleCountsRefresh = useCallback(() => {
    if (counts || !resourceScopeReady) return; // parent owns counts or access is unresolved
    if (countsRefreshTimer.current)
      window.clearTimeout(countsRefreshTimer.current);
    countsRefreshTimer.current = window.setTimeout(() => {
      const now = Date.now();
      if (now - lastBypassRefreshAtRef.current < MIN_BYPASS_REFRESH_INTERVAL_MS) {
        return;
      }
      lastBypassRefreshAtRef.current = now;
      fetchDashboardCounts(true).catch(() => {
        /* ignore */
      }); // bypass cache on realtime events
    }, 600); // debounce rapid bursts (increased from 400)
  }, [counts, fetchDashboardCounts, resourceScopeReady]);

  useEffect(() => {
    if (!resourceScopeReady) {
      if (countsRefreshTimer.current) {
        window.clearTimeout(countsRefreshTimer.current);
        countsRefreshTimer.current = null;
      }
      return;
    }
    const unsub = realtime.subscribe((ev: RealtimeMessage) => {
      const eventType = typeof ev?.event === "string"
        ? ev.event
        : (typeof (ev as { event_type?: unknown }).event_type === "string"
          ? String((ev as { event_type?: unknown }).event_type)
          : "");
      const tableName = typeof ev?.table === "string" ? ev.table.trim().toLowerCase() : "";
      if (eventAffectsSidebarCounters(eventType, tableName)) {
        scheduleCountsRefresh();
      }
    });
    return () => {
      if (typeof unsub === "function") unsub();
      if (countsRefreshTimer.current)
        window.clearTimeout(countsRefreshTimer.current);
    };
  }, [resourceScopeReady, realtime, scheduleCountsRefresh]);

  const effectiveCounts: Counts | undefined = resourceScopeReady
    ? (counts ?? ownCounts)
    : undefined;
  const hasEffectiveCounts =
    !!effectiveCounts &&
    !Object.values(effectiveCounts).every((v) => typeof v === "undefined");
  const loading = !hasEffectiveCounts;
  const branchPending = effectiveCounts?.pending;

  return {
    effectiveCounts,
    loading,
    branchPending,
  };
}
